import { useEffect, useState, type JSX } from "react";
import { useDispatch, useSelector } from "react-redux";

import { OptimizelyExperimentsEnum } from "@/config/app-constant";
import { useSubmitCodes } from "@/hooks/api/review-api/apply-promo";
import { RootState } from "@/store/root-reducer";
import { updateQuote } from "@/store/slices/quote/quote.slice";
import { QuoteData } from "@/types/quote/quote-data";

interface UsePromoBannerReturn {
  isPromoBannerActive: boolean;
  getPriceComparison: (
    total: number,
    discountedTotal: number | undefined,
    showPrice: (value: number | undefined) => string
  ) => JSX.Element | string;
  applyPromoCode: () => Promise<void>;
  isButtonDisabled: boolean;
  newQuote: QuoteData | undefined;
}

export const usePromoBanner = (
  promoCode?: string,
  bannerStatus?: "disabled" | "experiment" | "enabled"
): UsePromoBannerReturn => {
  const [isPromoBannerActive, setIsPromoBannerActive] = useState(false);
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const dispatch = useDispatch();

  const { version, type, attributes, referenceId } = useSelector(
    (state: RootState) => state.quote
  );

  const [newQuote, submitCodes] = useSubmitCodes(
    { version, type, attributes, referenceId },
    promoCode || "",
    [],
    setButtonDisabled
  ) as [QuoteData | undefined, () => Promise<void>];

  useEffect(() => {
    // If bannerStatus is provided (from PromoCodeBanner component), use it
    // Otherwise, check sessionStorage for banner status set by parent component
    const effectiveBannerStatus =
      bannerStatus ||
      (sessionStorage.getItem("promoBannerStatus") as
        | "disabled"
        | "experiment"
        | "enabled"
        | null);

    if (effectiveBannerStatus === "disabled") {
      setIsPromoBannerActive(false);
    } else if (effectiveBannerStatus === "enabled") {
      setIsPromoBannerActive(true);
    } else {
      const experimentFlag = localStorage.getItem(
        OptimizelyExperimentsEnum.PROMO_BANNER
      );
      setIsPromoBannerActive(experimentFlag === "variation");
    }
  }, [bannerStatus]);

  useEffect(() => {
    if (newQuote) {
      dispatch(updateQuote(newQuote));
    }
  }, [newQuote, dispatch]);

  const getPriceComparison = (
    total: number,
    discountedTotal: number | undefined,
    showPrice: (value: number | undefined) => string
  ): JSX.Element | string => {
    // Show strikethrough comparison when promo banner is active and discount exists
    if (isPromoBannerActive && discountedTotal && discountedTotal < total) {
      return (
        <>
          <span className="line-through text-2xs leading-[24px] font-normal opacity-70 mr-2">
            {showPrice(total)}
          </span>
          {showPrice(discountedTotal)}
        </>
      );
    }

    return showPrice(discountedTotal ?? total);
  };

  const applyPromoCode = async () => {
    await submitCodes();
  };

  return {
    isPromoBannerActive,
    getPriceComparison,
    applyPromoCode,
    isButtonDisabled: buttonDisabled,
    newQuote,
  };
};
